// src/context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const LS_KEY = "theme_pref"; // 'system' | 'light' | 'dark' — read synchronously in public/index.html too
const ORDER = ["system", "light", "dark"];

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [pref, setPref] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return ORDER.includes(saved) ? saved : "system";
    } catch { return "system"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (pref === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", pref);
    try { localStorage.setItem(LS_KEY, pref); } catch {}

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => {
      const effectiveDark = pref === "dark" || (pref === "system" && mql.matches);
      root.style.colorScheme = effectiveDark ? "dark" : "light";
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute("content", effectiveDark ? "#0a0a0b" : "#faf8f5");
    };
    sync();

    // Only matters while pref === "system" — OS can flip at runtime (e.g. a
    // scheduled dark-mode switch) and the meta/colorScheme tags don't track
    // @media changes on their own the way CSS custom properties do.
    if (pref === "system") {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }
  }, [pref]);

  const cycle = useCallback(() => {
    setPref((p) => ORDER[(ORDER.indexOf(p) + 1) % ORDER.length]);
  }, []);

  return (
    <ThemeContext.Provider value={{ pref, setPref, cycle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
